/**
 * Mobile-First Responsive Utilities
 * Provides responsive components and hooks for mobile-first design
 */

import React, { useState, useEffect, useCallback } from 'react';
import { HomeHeroNastaliqPoem } from '../HomeHeroPersianDecor';

// ===== RESPONSIVE HOOKS =====

/**
 * Hook to detect screen size and orientation
 */
export const useResponsive = () => {
    const [screenSize, setScreenSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
        isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
        isTablet: typeof window !== 'undefined' ? window.innerWidth >= 768 && window.innerWidth < 1024 : false,
        isDesktop: typeof window !== 'undefined' ? window.innerWidth >= 1024 : false,
        orientation: typeof window !== 'undefined' ? (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait') : 'portrait'
    });

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;

            setScreenSize({
                width,
                height,
                isMobile: width < 768,
                isTablet: width >= 768 && width < 1024,
                isDesktop: width >= 1024,
                orientation: width > height ? 'landscape' : 'portrait'
            });
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        // Initial call
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    return screenSize;
};

/**
 * Hook for touch-friendly interactions
 */
export const useTouchFriendly = () => {
    const [isTouchDevice, setIsTouchDevice] = useState(false);

    useEffect(() => {
        const checkTouchDevice = () => {
            setIsTouchDevice(
                'ontouchstart' in window ||
                navigator.maxTouchPoints > 0 ||
                // @ts-ignore
                navigator.msMaxTouchPoints > 0
            );
        };

        checkTouchDevice();
    }, []);

    return {
        isTouchDevice,
        touchTargetClass: isTouchDevice ? 'touch-target' : '',
        touchTargetLargeClass: isTouchDevice ? 'touch-target-large' : '',
        touchTargetXLClass: isTouchDevice ? 'touch-target-xl' : ''
    };
};

/**
 * Hook for orientation change handling
 */
export const useOrientation = () => {
    const { orientation, width, height } = useResponsive();
    const [isLandscapeCompact, setIsLandscapeCompact] = useState(false);

    useEffect(() => {
        setIsLandscapeCompact(orientation === 'landscape' && height < 500);
    }, [orientation, height]);

    return {
        orientation,
        isLandscape: orientation === 'landscape',
        isPortrait: orientation === 'portrait',
        isLandscapeCompact,
        orientationClass: orientation === 'landscape' ? 'landscape' : 'portrait',
        compactClass: isLandscapeCompact ? 'mobile-landscape-compact' : ''
    };
};

// ===== RESPONSIVE COMPONENTS =====

/**
 * Mobile-first responsive container
 */
interface ResponsiveContainerProps {
    children: React.ReactNode;
    className?: string;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
    padding?: 'none' | 'sm' | 'md' | 'lg';
}

export const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
    children,
    className = '',
    maxWidth = 'lg',
    padding = 'md'
}) => {
    const maxWidthClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
        '2xl': 'max-w-7xl',
        full: 'max-w-full'
    };

    const paddingClasses = {
        none: '',
        sm: 'px-4 sm:px-6',
        md: 'px-4 sm:px-6 lg:px-8',
        lg: 'px-6 sm:px-8 lg:px-12'
    };

    return (
        <div className={`container-mobile ${maxWidthClasses[maxWidth]} ${paddingClasses[padding]} ${className}`}>
            {children}
        </div>
    );
};

/**
 * Mobile-first responsive grid
 */
interface ResponsiveGridProps {
    children: React.ReactNode;
    columns?: 1 | 2 | 3 | 4;
    gap?: 'sm' | 'md' | 'lg';
    className?: string;
}

export const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
    children,
    columns = 3,
    gap = 'md',
    className = ''
}) => {
    const gridClasses = {
        1: 'grid-mobile-1',
        2: 'grid-mobile-2',
        3: 'grid-mobile-3',
        4: 'grid-mobile-4'
    };

    const gapClasses = {
        sm: 'gap-3',
        md: 'gap-4 sm:gap-6',
        lg: 'gap-6 sm:gap-8'
    };

    return (
        <div className={`${gridClasses[columns]} ${gapClasses[gap]} ${className}`}>
            {children}
        </div>
    );
};

/**
 * Mobile-first responsive button
 */
interface ResponsiveButtonProps {
    children: React.ReactNode;
    onClick?: () => void;
    variant?: 'primary' | 'secondary' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    disabled?: boolean;
    className?: string;
    type?: 'button' | 'submit' | 'reset';
}

export const ResponsiveButton: React.FC<ResponsiveButtonProps> = ({
    children,
    onClick,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    disabled = false,
    className = '',
    type = 'button'
}) => {
    const { touchTargetClass } = useTouchFriendly();

    const variantClasses = {
        primary: 'btn-mobile-primary',
        secondary: 'btn-mobile-secondary',
        outline: 'btn-mobile border-2 border-purple-600 text-purple-600 hover:bg-purple-50 active:bg-purple-100'
    };

    const sizeClasses = {
        sm: 'px-3 py-2 text-sm',
        md: 'px-4 py-3 text-base',
        lg: 'btn-mobile-large'
    };

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={`
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${touchTargetClass}
        ${fullWidth ? 'w-full' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
        >
            {children}
        </button>
    );
};

/**
 * Mobile-first responsive card
 */
interface ResponsiveCardProps {
    children: React.ReactNode;
    interactive?: boolean;
    padding?: 'sm' | 'md' | 'lg';
    className?: string;
    onClick?: () => void;
}

export const ResponsiveCard: React.FC<ResponsiveCardProps> = ({
    children,
    interactive = false,
    padding = 'md',
    className = '',
    onClick
}) => {
    const paddingClasses = {
        sm: 'p-3',
        md: 'p-4 sm:p-6',
        lg: 'p-6 sm:p-8'
    };

    const cardClass = interactive ? 'card-mobile-interactive' : 'card-mobile';

    return (
        <div
            className={`${cardClass} ${paddingClasses[padding]} ${className}`}
            onClick={onClick}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
        >
            {children}
        </div>
    );
};

/**
 * Mobile-first responsive form input
 */
interface ResponsiveInputProps {
    type?: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    persian?: boolean;
}

export const ResponsiveInput: React.FC<ResponsiveInputProps> = ({
    type = 'text',
    value,
    onChange,
    placeholder,
    label,
    error,
    required = false,
    disabled = false,
    className = '',
    persian = false
}) => {
    const inputClass = persian ? 'persian-input-mobile' : 'form-input-mobile';

    return (
        <div className="form-mobile">
            {label && (
                <label className="form-label-mobile">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                className={`${inputClass} ${error ? 'border-red-500 focus:ring-red-500' : ''} ${className}`}
            />
            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
};

/**
 * Mobile-first responsive modal
 */
interface ResponsiveModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    className?: string;
}

export const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    className = ''
}) => {
    const { isMobile } = useResponsive();

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="modal-mobile" onClick={onClose}>
            <div className="flex items-end sm:items-center justify-center min-h-full p-4">
                <div
                    className={`modal-content-mobile animate-mobile-slide-up ${className}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {title && (
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="heading-mobile-md">{title}</h2>
                            <button
                                onClick={onClose}
                                className="touch-target text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {children}
                </div>
            </div>
        </div>
    );
};

/**
 * Mobile-first responsive navigation
 */
interface ResponsiveNavProps {
    children: React.ReactNode;
    className?: string;
    sticky?: boolean;
    /** Learner dashboard chrome. Simple keeps the same controls with a quieter bar. */
    chrome?: 'default' | 'learner' | 'learner-simple';
}

export const ResponsiveNav: React.FC<ResponsiveNavProps> = ({
    children,
    className = '',
    sticky = true,
    chrome = 'default'
}) => {
    const { isLandscapeCompact } = useOrientation();
    const learner = chrome === 'learner' || chrome === 'learner-simple';

    return (
        <nav
            className={`
        ${sticky ? 'nav-mobile' : 'bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700'}
        ${learner ? 'overflow-hidden' : ''}
        ${isLandscapeCompact ? 'mobile-landscape-nav' : ''}
        safe-area-top
        ${className}
      `}
            style={sticky ? {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 50,
                backgroundColor: 'rgba(9, 38, 66, 0.2)',
                backdropFilter: 'blur(12px)',
                borderBottom: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 8px 25px -5px rgba(0, 0, 0, 0.1)'
            } : {}}
        >
            {learner ? <HomeHeroNastaliqPoem variant="header" /> : null}
            <div className="container-mobile relative z-10">
                <div className="flex items-center justify-between h-16">
                    {children}
                </div>
            </div>
        </nav>
    );
};

/**
 * Responsive visibility components
 */
export const MobileOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="mobile-only">{children}</div>
);

export const TabletUp: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="tablet-up">{children}</div>
);

export const DesktopOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="desktop-only">{children}</div>
);

/**
 * Touch-friendly icon button
 */
interface TouchIconButtonProps {
    icon: React.ReactNode;
    onClick: () => void;
    label: string;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'primary' | 'secondary' | 'ghost';
    className?: string;
    dataTourId?: string;
}

export const TouchIconButton: React.FC<TouchIconButtonProps> = ({
    icon,
    onClick,
    label,
    size = 'md',
    variant = 'ghost',
    className = '',
    dataTourId,
}) => {
    const { touchTargetClass } = useTouchFriendly();

    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12'
    };

    const variantClasses = {
        primary: 'bg-purple-600 text-white hover:bg-purple-700',
        secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
        ghost: 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
    };

    return (
        <button
            onClick={onClick}
            data-tour-id={dataTourId}
            className={`
        ${touchTargetClass}
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        rounded-full flex items-center justify-center
        transition-all duration-200
        active:scale-95 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
        ${className}
      `}
            aria-label={label}
            title={label}
        >
            {icon}
        </button>
    );
};

export default {
    useResponsive,
    useTouchFriendly,
    useOrientation,
    ResponsiveContainer,
    ResponsiveGrid,
    ResponsiveButton,
    ResponsiveCard,
    ResponsiveInput,
    ResponsiveModal,
    ResponsiveNav,
    MobileOnly,
    TabletUp,
    DesktopOnly,
    TouchIconButton
};